import React from 'react';
import AutoCompleteField from '../../core/components/Fields/AutoCompleteField';
import { useAssistanceFieldContext } from '../pages/Assistance/AssistanceFieldContext';
import { renderLookupOptionWithLineItemCount, deduplicateLookupOptions } from './useLookup';
import { FieldProps, useFieldProps } from '../pages/Assistance/customizable/Field';
import useForceSelectionLookupProps from './useForceSelectionLookupProps';
import useReselectEventListener from '../pages/Assistance/useReselectEventListener';
import StringField from '../../core/components/Fields/StringField';

type MatchingLookupFieldConfig = {
    lookupType: 'OfferMatchingLookupType' | 'OrderMatchingLookupType';
    primaryKey: 'offerNumber' | 'orderNumber';
};

// Factory for assistance fields that browse open offers/orders via a typeahead lookup against
// the master data service. The QuoteNumberField (offer matching on orders) and OrderNumberField
// (order matching on order confirmations) are configured instances of this component.
export const createMatchingLookupField =
    ({ lookupType, primaryKey }: MatchingLookupFieldConfig) =>
    (props: FieldProps) => {
        const { clientPartitionId, config } = useAssistanceFieldContext();
        const { lookupDefinitionId, lookupFieldName, allowInvalidIds, maxFieldLength } = config;

        const fieldProps = useFieldProps(props);
        const { value, onValueChange, onFocus, onBlur, onCommit } = fieldProps;

        const forceSelectionLookupProps = useForceSelectionLookupProps({
            lookupType,
            lookupDefinitionId,
            lookupFieldName,
            partitionId: clientPartitionId,
            allowInvalidIds: allowInvalidIds ?? true,
            value,
            onValueChange,
            onUpdatePayloadChange: props.onUpdatePayloadChange,
            onFocus,
            onBlur,
            onCommit,
            lookupMatchingProps: {
                searchLookupOnEmptyValue: true,
                processOptions: (options) => deduplicateLookupOptions(options, primaryKey),
                // TODO: replace with a server-side aggregation query (primaryKey + count(*)),
                // in case we run into this limit often. Then, line item counts are accurate
                // regardless of how many line items a single document has.
                limit: 500,
                alwaysShowBrowser: true,
            },
        });

        useReselectEventListener(props.inputRef, props.onReselect);

        // If no lookup is provided return this field as a StringField
        if (!lookupDefinitionId || !lookupFieldName) {
            return <StringField {...fieldProps} maxLength={maxFieldLength} />;
        }

        return (
            <AutoCompleteField
                {...fieldProps}
                {...forceSelectionLookupProps}
                maxLength={maxFieldLength}
                renderOption={renderLookupOptionWithLineItemCount}
            />
        );
    };
